import React from 'react';
import { Scrollama, Step } from 'react-scrollama';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { get } from 'lodash';
import slugify from 'slugify';
import Layout from '~components/Layout';
import Topper from '~components/base/Topper';
import BodyElement from '~components/body/BodyElement';
import ArchieElement from '~components/body/ArchieElement';
import Byline from '~components/base/Bylines';
import SeoSocial from '~components/base/SeoSocial';
import useBusiness from '~lib/useBusiness';
import YearStats from '~components/graphics/YearStats';
import GlobalStyles from '../styles/GlobalStyles';

export const pageQuery = graphql`
    query {
        site {
          pathPrefix
          siteMetadata {
            title
            sectionName
            projectName
            pubDate
          }
        }
        story: arcStory(storyId: {eq: "IX5SG6YI6NDVBGN7WTUUTV7BOY"}) {
            headlines {
                basic
            }
            subheadlines {
                basic
            }
            credits {
                name
                url
            }
            bodyElements {
              _id
              type
              subtype
              alignment
              content
              archieJson
              url
              srcSet
              caption
              subtype
              bodyElements {
                _id
                content
              }
              citation {
                content
              }
              creditLine
              credits {
                name
              }
            }
            # getting seo social data
            promoImage {
              sizes {
                 _1024
              }
            }
            description {
              basic
            }
            # getting teasers information
            relatedLinks {
              headlines {
                basic
              }
              promoImage {
                sizes {
                  # _1024
                  _480
                  _640
                  _240
                }
                srcSet
                url
              }
              url
            }
            slug
        }
    }
`;

const ArticleTemplate = ({
  data: {
    story,
    site: {
      pathPrefix,
      siteMetadata: { sectionName, projectName, pubDate },
    },
  },
}) => {
  const slugifiedSection = slugify(sectionName, { lower: true });
  const authorNames = get(story, 'credits', [])
    .map((credit) => credit.name)
    .join(', ');

  useBusiness({
    pageName: `${slugifiedSection} | ${get(story, 'headlines.basic')}`,
    environment: process.env.NODE_ENV,
    meterContent: (process.env.NODE_ENV !== 'development'),
    pageId: 'apps.david-ortiz-timeline',
    section: slugifiedSection,
    author: authorNames,
    projectName,
  });

  const subhed = get(story, 'subheadlines.basic')
    ? (<h3 className="subheadline miller-n4">{get(story, 'subheadlines.basic')}</h3>)
    : (null);
  const metaUrl = `https://apps.bostonglobe.com${pathPrefix}`;

  const [currentStepIndex, setCurrentStepIndex] = React.useState(null);

  const onStepEnter = ({ data }) => {
    // console.log(data);
    setCurrentStepIndex(data);
  };

  return (
    <Layout>
      <SeoSocial
        url={metaUrl}
        description={get(story, 'description.basic')}
        title="David Ortiz's MLB career through the years"
        imageUrl={get(story, 'promoImage.sizes._1024')}
      />
      <GlobalStyles />
      <Topper>
        <h1 className="headline millerbanner-n4">{get(story, 'headlines.basic')}</h1>
        {subhed}
        <Byline
          data={get(story, 'credits')}
          pubDate={pubDate}
        />
      </Topper>
      <Scrollama
        onStepEnter={onStepEnter}
        offset="0.5"
        // debug
      >
        {story.bodyElements.map((element, stepIndex) => {
          if (element.archieJson) {
            return (
              <Step data={stepIndex} key={element._id}>
                <div>
                  <ArchieElement
                    data={element}
                    relatedLinks={get(story, 'relatedLinks')}
                  />
                </div>
              </Step>
            );
          }
          if (element.type === 'header') {
            const inView = (currentStepIndex >= stepIndex)
              ? 'inView'
              : '';
            return (
              <Step data={stepIndex} key={element._id}>
                <div>
                  <YearStats inView={inView} year={element.content} />
                </div>
              </Step>
            );
          }
          return (
            <Step data={stepIndex} key={element._id}>
              <div>
                <BodyElement
                  data={element}
                />
              </div>
            </Step>

          );
        })}
      </Scrollama>
    </Layout>
  );
};

ArticleTemplate.propTypes = {
  data: PropTypes.shape({
    story: PropTypes.shape({
      bodyElements: PropTypes.arrayOf(
        PropTypes.shape({
          archieJson: PropTypes.string,
        }),
      ),
      slug: PropTypes.string,
    }),
    site: PropTypes.shape({
      pathPrefix: PropTypes.string.isRequired,
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
        sectionName: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
        pubDate: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    storyId: PropTypes.string,
  }).isRequired,
};

export default ArticleTemplate;
